import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { LayerState } from '@/api/generated/schemas';
import { Button, Panel } from '@/shared/ui';
import { SerieStateBadge, type SerieLayerKey } from '../../../serie';

interface TermLayerPanelProps {
  /** Klíč vrstvy – titulek panelu (`calendar.drawer.layers.<key>.title`). */
  layerKey: SerieLayerKey;
  state: LayerState;
  /** Krátké shrnutí pod hlavičkou (dědí ze série / vlastní schéma…). */
  summary?: ReactNode;
  /** Editor uvnitř panelu (jen dopravní vrstva); u read-only vrstev se vynechá. */
  children?: ReactNode;
  /** Otevře detail termínu na příslušné záložce. Bez něj se odkaz nevykreslí. */
  onOpenDetail?: () => void;
  /** Schvalovací tlačítko. Vrstva s vlastním editorem si ho vkládá do svého obsahu. */
  approveAction?: ReactNode;
  defaultOpen?: boolean;
}

/**
 * Jeden panel vrstvy v sidebaru kalendáře – jednotný rám pro všechny vrstvy termínu (stav termínu,
 * doprava, product cost, kalkulace). Vrstvy bez editoru (boardy žijí v detailu termínu) ukazují jen
 * stav, shrnutí a schvalovací tlačítko + odkaz do detailu.
 */
export function TermLayerPanel({
  layerKey,
  state,
  summary,
  children,
  onOpenDetail,
  approveAction,
  defaultOpen,
}: TermLayerPanelProps) {
  const { t } = useTranslation('venua-planner');

  const hasFooter = Boolean(onOpenDetail || approveAction);

  return (
    <Panel
      variant="filled"
      title={t(`calendar.drawer.layers.${layerKey}.title`)}
      badge={<SerieStateBadge state={state} />}
      collapsible
      defaultOpen={defaultOpen ?? state === LayerState.Draft}
    >
      <div className="gap-phx-3 flex flex-col">
        {summary ? <p className="text-phx-sm text-phx-text-muted">{summary}</p> : null}

        {children}

        {hasFooter ? (
          <div className="gap-phx-2 flex items-center justify-between">
            {onOpenDetail ? (
              <Button variant="link" size="sm" iconRight="external-link" onClick={onOpenDetail}>
                {t('calendar.drawer.layers.openInDetail')}
              </Button>
            ) : (
              <span />
            )}
            {approveAction}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
